import React from 'react';
import { bind } from "decko";
import PropTypes from "prop-types";

import PossessionTrackingDisplay from "./PossessionTrackingDisplay";

const eventCmp = (a, b) => a.start - b.start;

export default class PossessionTracking extends React.Component {
    constructor (props) {
        super(props);

        this.hotkeys = this.createHotkeys(props);

        this.state = {};
        this.events = [];
        this.streamRecordingTime = new Date(props.streamRecordingTime);
    }

    createHotkeys (props) {

        let teamIds = [props.game.home_team.id, props.game.visiting_team.id];
        let commands = [
            () => this.onChangePossession(teamIds[0]),
            () => this.onChangePossession(null),
            () => this.onChangePossession(teamIds[1]),
        ];

        switch (props.quadStreamIdx) {
            case 0:
                return {
                    "z": commands[0],
                    "x": commands[1],
                    "c": commands[2],
                };
            case 1:
                return {
                    "arrowLeft":  commands[0],
                    "arrowDown":  commands[1],
                    "arrowRight": commands[2],
                };
            case 2:
                return {
                    "s+z": commands[0],
                    "s+x": commands[1],
                    "s+c": commands[2],
                };
            case 3:
                return {
                    "s+arrowLeft":  commands[0],
                    "s+arrowDown":  commands[1],
                    "s+arrowRight": commands[2],
                };
            case 4: //FULLSCREEN - support both
                return {
                    "arrowLeft":  commands[0],
                    "arrowDown":  commands[1],
                    "arrowRight": commands[2],
                    "z": commands[0],
                    "x": commands[1],
                    "c": commands[2],
                };
            default:
                return undefined;
        }
    }

    componentDidMount () {

        // Load possession events from server
        let possessionUrl = "/game/" + this.props.game.id + "/possessions";
        this.props.server.get(possessionUrl,
            (data) => {
                this.events = data.possessions.map(e => ({
                    teamId: e.team_id,
                    start: this.timestampToOffset(e.from_ts),
                    end: this.timestampToOffset(e.to_ts),
                }));
            },
            (error) => this.props.onError("Faled to load possession events: " + error)
        );

        if (this.context.globalHotkeyHandler) {
            console.log("Subscribing", this.hotkeys);
            this.context.globalHotkeyHandler.subscribe(this.hotkeys, this);
        }
    }

    componentWillUnmount () {
        if (this.context.globalHotkeyHandler) {
            console.log("Unsubscribing", this.hotkeys);
            this.context.globalHotkeyHandler.unsubscribe(this.hotkeys, this);
        }
    }

    offsetToTimestamp (offset) {
        let ts = new Date(this.streamRecordingTime.getTime());
        ts.setMilliseconds(ts.getMilliseconds() + (offset * 1000));
        return ts.toISOString();
    }

    timestampToOffset (str) {
        return (Date.parse(str) - this.streamRecordingTime) / 1000;
    }

    createEvent (teamId) {
        let ev = {
            teamId: teamId,
            start: this.props.videoCurrentTime,
            end: null,
        };
        this.events.push(ev);
        this.events.sort(eventCmp);
        return ev;
    }

    getEventDuration (ev) {
        return ev.end - ev.start;
    }

    getActiveEventIndex () {
        let time = this.props.videoCurrentTime;
        let i = 0;
        for (; i < this.events.length; ++i) {
            let {start, end} = this.events[i];
            if ((end === null || time < end) && time >= start) return i;
        }
        return null;
    }

    getActiveEvent () {
        const idx = this.getActiveEventIndex();
        if (idx === null) return null;
        return this.events[idx];
    }

    postEvent (event) {
        let url = "/game/" + this.props.game.id + "/possession";
        this.props.server.post(url,
            {
                team_id: event.teamId,
                from_ts: this.offsetToTimestamp(event.start),
                to_ts: this.offsetToTimestamp(event.end),
            },
            () => undefined,
            (error) => this.props.onError("Failed to add possession event: " + error)
        );
    }

    deleteEvent (event) {
        let url = "/game/" + this.props.game.id + "/possession";
        this.props.server.delete(url,
            {
                team_id: event.teamId,
                from_ts: this.offsetToTimestamp(event.start),
                to_ts: this.offsetToTimestamp(event.end),
            },
            () => undefined,
            (error) => this.props.onError("Failed to delete possession event: " + error)
        );
    }

    removeOverwrittenEvents (activeEvent) {
        // Delete any events that were completely overwritten by activeEvent
        // Events that occur after activeEvent, but has overlap with activeEvent
        for (let i=0; i<this.events.length; ++i) {

            let e = this.events[i];

            if (e === activeEvent || e.start < activeEvent.start) continue;

            if (e.start >= activeEvent.end) break;

            if (e.end <= activeEvent.end) {
                // Full overlap - delete the entire thing and remove it from the local list
                if (e.teamId !== activeEvent.teamId) {
                    this.deleteEvent(e);
                }
                this.events.splice(i, 1);
                --i;
            } else {
                // Partial overlap - delete the overlapping area, and shorten local copy
                if (e.teamId !== activeEvent.teamId) {
                    this.deleteEvent({
                        teamId: e.teamId,
                        start: e.start,
                        end: activeEvent.end,
                    });
                }
                e.start = activeEvent.end;
            }
        }
    }

    removeEvent (ev) {
        let idx = this.events.indexOf(ev);
        if (idx >= 0) this.events.splice(idx, 1);
    }

    @bind
    onChangePossession (teamId) {

        let activeEvent = this.getActiveEvent();
        let activeTeamId = activeEvent === null ? null : activeEvent.teamId;

        if (activeTeamId === teamId) return;

        // End the current event first
        if (activeEvent !== null) {

            if (activeEvent.end === null) {

                // Standard path - end the current un-terminated event
                activeEvent.end = this.props.videoCurrentTime;

                // Skip saving really short events - mistakes
                if (this.getEventDuration(activeEvent) > 1) {
                    this.postEvent(activeEvent);
                } else {
                    this.removeEvent(activeEvent);
                }

            } else {
                // Current event is already terminated, this is a modification

                // Delete the rest of the current event
                let eventToDelete = {
                    teamId: activeEvent.teamId,
                    start: this.props.videoCurrentTime,
                    end: activeEvent.end,
                };

                activeEvent.end = this.props.videoCurrentTime;

                if (this.getEventDuration(activeEvent) < 1) {
                    // Delete the entire original event because it is so short (likely correcting a mistake)
                    // First reset the active event to it's original duration then remove it
                    activeEvent.end = eventToDelete.end;
                    this.removeEvent(activeEvent);
                    this.deleteEvent(activeEvent);
                } else {
                    this.deleteEvent(eventToDelete);
                }
            }

            // Clean up any overlapping events that were overwritten by this new one
            this.removeOverwrittenEvents(activeEvent);
        }

        // Start a new event - if applicable
        if (teamId !== null) this.createEvent(teamId);

        //Note: Only done for slightly faster UI refresh (most relevant when paused)
        this.setState({});
    }

    render () {

        let duration = null;
        let teamId = null;

        let activeEvent = this.getActiveEvent();
        if (activeEvent !== null) {
            duration = this.props.videoCurrentTime - activeEvent.start;
            teamId = activeEvent.teamId;
        }

        return (
            <PossessionTrackingDisplay
                homeTeam={this.props.game.home_team}
                visitingTeam={this.props.game.visiting_team}
                activeTeamId={teamId}
                possessionDuration={duration}
                homeTeamColor={this.props.homeTeamColor}
                visitingTeamColor={this.props.visitingTeamColor}
                onClick={this.onChangePossession}
            />
        );
    }
}

PossessionTracking.contextTypes = {
    globalHotkeyHandler: PropTypes.object
};


// WEBPACK FOOTER //
// ./src/js/PossessionTracking.js